import React, { useState, useEffect } from 'react';
import { Save, Loader2, MapPin, Settings, Truck, Phone, Mail, Building, Globe } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';

import { API_URL, BACKEND_URL } from '../../config/api';
const API = `${BACKEND_URL}/api`;

const AdminSettings = () => {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('services');

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await fetch(`${API}/admin/settings`, { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        setSettings(data);
      }
    } catch (error) {
      console.error('Error fetching settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const response = await fetch(`${API}/admin/settings`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(settings)
      });

      if (response.ok) {
        console.log('Success:', 'Paramètres enregistrés');
      } else {
        console.log('Error:', 'Erreur lors de l\'enregistrement');
      }
    } catch (error) {
      console.log('Error:', 'Erreur de connexion');
    } finally {
      setSaving(false);
    }
  };

  const updateZone = (index, field, value) => {
    const newZones = [...settings.installation_zones];
    newZones[index] = { ...newZones[index], [field]: parseFloat(value) || 0 };
    setSettings(prev => ({ ...prev, installation_zones: newZones }));
  };

  const updateContact = (field, value) => {
    setSettings(prev => ({
      ...prev,
      contact: { ...prev.contact, [field]: value }
    }));
  };

  const updateAddress = (field, value) => {
    setSettings(prev => ({
      ...prev,
      address: { ...prev.address, [field]: value }
    }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-fgs-primary" />
      </div>
    );
  }

  const tabs = [
    { id: 'services', label: 'Services', icon: Settings },
    { id: 'contact', label: 'Coordonnées', icon: Phone },
    { id: 'installation', label: 'Installation', icon: Truck },
  ];

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="font-heading text-2xl font-bold text-white">Paramètres</h1>
        <Button 
          onClick={handleSave}
          disabled={saving}
          className="bg-fgs-primary hover:bg-fgs-primary-hover"
        >
          {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
          Enregistrer
        </Button>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 mb-6 bg-fgs-bg p-1 rounded-lg w-fit">
        {tabs.map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-2 px-4 py-2 rounded text-sm font-medium transition-colors ${
              activeTab === tab.id
                ? 'bg-fgs-primary text-white'
                : 'text-fgs-text-muted hover:text-white'
            }`}
          >
            <tab.icon className="w-4 h-4" />
            {tab.label}
          </button>
        ))}
      </div>

      <div className="grid gap-6">
        {/* Contact Info Tab */}
        {activeTab === 'contact' && (
          <>
            <div className="bg-fgs-surface border border-fgs-border rounded-xl p-6">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 rounded-lg bg-fgs-primary/20 flex items-center justify-center">
                  <Phone className="w-5 h-5 text-fgs-primary" />
                </div>
                <div>
                  <h2 className="font-heading text-lg font-bold text-white">Contact</h2>
                  <p className="text-sm text-fgs-text-muted">Téléphone, email et WhatsApp</p>
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label className="text-fgs-text-muted text-sm">Téléphone (affiché)</Label>
                  <Input
                    value={settings?.contact?.phone || '07 45 04 09 29'}
                    onChange={(e) => updateContact('phone', e.target.value)}
                    placeholder="07 45 04 09 29"
                    className="bg-fgs-bg border-fgs-border text-white mt-1"
                  />
                </div>
                <div>
                  <Label className="text-fgs-text-muted text-sm">Téléphone (format international)</Label>
                  <Input
                    value={settings?.contact?.phoneFormatted || '+33745040929'}
                    onChange={(e) => updateContact('phoneFormatted', e.target.value)}
                    placeholder="+33745040929"
                    className="bg-fgs-bg border-fgs-border text-white mt-1"
                  />
                  <p className="text-xs text-fgs-text-muted mt-1">Utilisé pour les liens tel:</p>
                </div>
                <div>
                  <Label className="text-fgs-text-muted text-sm">WhatsApp (format international)</Label>
                  <Input
                    value={settings?.contact?.whatsapp || '+33745040929'}
                    onChange={(e) => updateContact('whatsapp', e.target.value)}
                    placeholder="+33745040929"
                    className="bg-fgs-bg border-fgs-border text-white mt-1"
                  />
                  <p className="text-xs text-fgs-text-muted mt-1">Utilisé pour les liens WhatsApp</p>
                </div>
                <div>
                  <Label className="text-fgs-text-muted text-sm">Email</Label>
                  <Input
                    type="email"
                    value={settings?.contact?.email || 'contact@fgs-security.fr'}
                    onChange={(e) => updateContact('email', e.target.value)}
                    placeholder="contact@fgs-security.fr"
                    className="bg-fgs-bg border-fgs-border text-white mt-1"
                  />
                </div>
              </div>
            </div>

            <div className="bg-fgs-surface border border-fgs-border rounded-xl p-6">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 rounded-lg bg-fgs-primary/20 flex items-center justify-center">
                  <Building className="w-5 h-5 text-fgs-primary" />
                </div>
                <div>
                  <h2 className="font-heading text-lg font-bold text-white">Adresse</h2>
                  <p className="text-sm text-fgs-text-muted">Adresse de l'entreprise</p>
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label className="text-fgs-text-muted text-sm">Rue</Label>
                  <Input
                    value={settings?.address?.street || '6 Bis Boulevard Berthelot'}
                    onChange={(e) => updateAddress('street', e.target.value)}
                    className="bg-fgs-bg border-fgs-border text-white mt-1"
                  />
                </div>
                <div>
                  <Label className="text-fgs-text-muted text-sm">Complément</Label>
                  <Input
                    value={settings?.address?.complement || 'Bureau 3'}
                    onChange={(e) => updateAddress('complement', e.target.value)}
                    placeholder="Bureau, Bâtiment..."
                    className="bg-fgs-bg border-fgs-border text-white mt-1"
                  />
                </div>
                <div>
                  <Label className="text-fgs-text-muted text-sm">Code postal</Label>
                  <Input
                    value={settings?.address?.postalCode || '34000'}
                    onChange={(e) => updateAddress('postalCode', e.target.value)}
                    className="bg-fgs-bg border-fgs-border text-white mt-1"
                  />
                </div>
                <div>
                  <Label className="text-fgs-text-muted text-sm">Ville</Label>
                  <Input
                    value={settings?.address?.city || 'MONTPELLIER'}
                    onChange={(e) => updateAddress('city', e.target.value)}
                    className="bg-fgs-bg border-fgs-border text-white mt-1"
                  />
                </div>
              </div>

              <div className="mt-4">
                <Label className="text-fgs-text-muted text-sm">URL Google Maps (embed)</Label>
                <Input
                  value={settings?.address?.mapsEmbed || ''}
                  onChange={(e) => updateAddress('mapsEmbed', e.target.value)}
                  placeholder="https://www.google.com/maps/embed?..."
                  className="bg-fgs-bg border-fgs-border text-white mt-1"
                />
                <p className="text-xs text-fgs-text-muted mt-1">
                  Utilisé pour afficher la carte sur le site
                </p>
              </div>
            </div>

            <div className="bg-fgs-surface border border-fgs-border rounded-xl p-6">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 rounded-lg bg-fgs-primary/20 flex items-center justify-center">
                  <Globe className="w-5 h-5 text-fgs-primary" />
                </div>
                <div>
                  <h2 className="font-heading text-lg font-bold text-white">Réseaux sociaux</h2>
                  <p className="text-sm text-fgs-text-muted">Liens vers vos réseaux</p>
                </div>
              </div>

              <div className="grid md:grid-cols-3 gap-4">
                <div>
                  <Label className="text-fgs-text-muted text-sm">Facebook</Label>
                  <Input
                    value={settings?.social?.facebook || ''}
                    onChange={(e) => setSettings(prev => ({
                      ...prev,
                      social: { ...prev.social, facebook: e.target.value }
                    }))}
                    placeholder="https://facebook.com/..."
                    className="bg-fgs-bg border-fgs-border text-white mt-1"
                  />
                </div>
                <div>
                  <Label className="text-fgs-text-muted text-sm">LinkedIn</Label>
                  <Input
                    value={settings?.social?.linkedin || ''}
                    onChange={(e) => setSettings(prev => ({
                      ...prev,
                      social: { ...prev.social, linkedin: e.target.value }
                    }))}
                    placeholder="https://linkedin.com/..."
                    className="bg-fgs-bg border-fgs-border text-white mt-1"
                  />
                </div>
                <div>
                  <Label className="text-fgs-text-muted text-sm">Instagram</Label>
                  <Input
                    value={settings?.social?.instagram || ''}
                    onChange={(e) => setSettings(prev => ({
                      ...prev,
                      social: { ...prev.social, instagram: e.target.value }
                    }))}
                    placeholder="https://instagram.com/..."
                    className="bg-fgs-bg border-fgs-border text-white mt-1"
                  />
                </div>
              </div>
            </div>

            <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-xl p-4">
              <p className="text-yellow-400 text-sm">
                <strong>Note :</strong> Pour que les modifications de coordonnées s'affichent sur le site, 
                il faudra mettre à jour le fichier de configuration frontend. 
                Contactez votre développeur pour appliquer ces changements.
              </p>
            </div>
          </>
        )}

        {/* Services Tab */}
        {activeTab === 'services' && (
          <div className="bg-fgs-surface border border-fgs-border rounded-xl p-6">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-lg bg-fgs-primary/20 flex items-center justify-center">
                <Settings className="w-5 h-5 text-fgs-primary" />
              </div>
              <div>
                <h2 className="font-heading text-lg font-bold text-white">Services optionnels</h2>
                <p className="text-sm text-fgs-text-muted">Tarifs des services pour HUB et Kits</p>
              </div>
            </div>

            <div className="grid md:grid-cols-3 gap-4">
              <div>
                <Label className="text-fgs-text-muted text-sm">Programmation seule (€)</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={settings?.service_programming_price || 150}
                  onChange={(e) => setSettings(prev => ({ 
                    ...prev, 
                    service_programming_price: parseFloat(e.target.value) || 0 
                  }))}
                  className="bg-fgs-bg border-fgs-border text-white mt-1"
                />
              </div>
              <div>
                <Label className="text-fgs-text-muted text-sm">Forfait 4G mensuel (€/mois)</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={settings?.service_4g_monthly || 8.90}
                  onChange={(e) => setSettings(prev => ({ 
                    ...prev, 
                    service_4g_monthly: parseFloat(e.target.value) || 0 
                  }))}
                  className="bg-fgs-bg border-fgs-border text-white mt-1"
                />
              </div>
              <div>
                <Label className="text-fgs-text-muted text-sm">Forfait 4G annuel (€/an)</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={settings?.service_4g_annual || 99}
                  onChange={(e) => setSettings(prev => ({ 
                    ...prev, 
                    service_4g_annual: parseFloat(e.target.value) || 0 
                  }))}
                  className="bg-fgs-bg border-fgs-border text-white mt-1"
                />
              </div>
            </div>
          </div>
        )}

        {/* Installation Tab */}
        {activeTab === 'installation' && (
          <>
            <div className="bg-fgs-surface border border-fgs-border rounded-xl p-6">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 rounded-lg bg-fgs-primary/20 flex items-center justify-center">
                  <Truck className="w-5 h-5 text-fgs-primary" />
                </div>
                <div>
                  <h2 className="font-heading text-lg font-bold text-white">Zones d'installation</h2>
                  <p className="text-sm text-fgs-text-muted">Tarifs selon la distance depuis {settings?.installation_origin || 'Montpellier'}</p>
                </div>
              </div>

              <div className="mb-4">
                <Label className="text-fgs-text-muted text-sm">Ville d'origine</Label>
                <Input
                  value={settings?.installation_origin || 'Montpellier'}
                  onChange={(e) => setSettings(prev => ({ 
                    ...prev, 
                    installation_origin: e.target.value 
                  }))}
                  className="bg-fgs-bg border-fgs-border text-white mt-1 max-w-xs"
                />
              </div>

              <div className="space-y-3">
                {settings?.installation_zones?.map((zone, index) => (
                  <div key={index} className="flex items-center gap-4 p-4 bg-fgs-bg rounded-lg">
                    <MapPin className="w-5 h-5 text-fgs-text-muted" />
                    <div className="flex items-center gap-2">
                      <span className="text-fgs-text-muted text-sm">Jusqu'à</span>
                      <Input
                        type="number"
                        value={zone.max_km}
                        onChange={(e) => updateZone(index, 'max_km', e.target.value)}
                        className="w-20 bg-fgs-surface border-fgs-border text-white text-center"
                      />
                      <span className="text-fgs-text-muted text-sm">km</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-fgs-text-muted text-sm">=</span>
                      <Input
                        type="number"
                        step="0.01"
                        value={zone.price}
                        onChange={(e) => updateZone(index, 'price', e.target.value)}
                        className="w-24 bg-fgs-surface border-fgs-border text-white text-center"
                      />
                      <span className="text-fgs-primary font-bold">€</span>
                    </div>
                  </div>
                ))}
              </div>

              <p className="text-xs text-fgs-text-muted mt-4">
                Au-delà de la dernière zone, les clients verront "Ville pas encore desservie"
              </p>
            </div>

            <div className="bg-blue-500/10 border border-blue-500/30 rounded-xl p-4">
              <p className="text-blue-400 text-sm">
                <strong>Note :</strong> Les modifications de tarifs s'appliqueront aux nouvelles commandes. 
                Les commandes existantes conservent leurs tarifs d'origine.
              </p>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default AdminSettings;
